import { useEffect, useState } from "react";
import { useFormatter } from "@/artifacts/format";

/**
 * "3 minutes ago", in the deployment's locale.
 *
 * A refreshed-at stamp is the difference between a report and a screenshot
 * (PRD FR-7.4), and an absolute timestamp makes the reader do the arithmetic.
 * It re-renders on a slow interval so a report left open does not keep
 * claiming to be a minute old an hour later.
 *
 * `Intl.RelativeTimeFormat` rather than a table of English strings: the locale
 * is `MING_LOCALE`, the same setting that decides how money reads.
 */
const STEPS: Array<[Intl.RelativeTimeFormatUnit, number]> = [
  ["second", 60],
  ["minute", 60],
  ["hour", 24],
  ["day", 7],
  ["week", 4.35],
  ["month", 12],
  ["year", Number.POSITIVE_INFINITY],
];

export function relativeTime(value: string | null | undefined, locale: string): string {
  if (!value) return "";
  const then = new Date(value).getTime();
  if (Number.isNaN(then)) return "";
  const format = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });

  let amount = (then - Date.now()) / 1000;
  for (const [unit, span] of STEPS) {
    if (Math.abs(amount) < span) return format.format(Math.round(amount), unit);
    amount /= span;
  }
  return format.format(Math.round(amount), "year");
}

export function useRelativeTime(value: string | null | undefined): string {
  // The same locale money is formatted in — `MING_LOCALE`, never a literal.
  const { locale } = useFormatter();
  const [, tick] = useState(0);
  useEffect(() => {
    if (!value) return;
    const timer = window.setInterval(() => tick((count) => count + 1), 30_000);
    return () => window.clearInterval(timer);
  }, [value]);
  return relativeTime(value, locale);
}
